import { RefObject, useLayoutEffect, useRef } from 'react'

/**
 * Remembers the element focused when `show` turns true and focuses it again
 * when `show` turns false, unless focus moved outside the container meanwhile.
 * Call it before any hook that moves focus into the container on open, so the
 * opener is read first.
 */
export function useRestoreFocusOnClose(
  show: boolean,
  containerRef: RefObject<HTMLElement>
): void {
  const openerRef = useRef<HTMLElement | null>(null)

  useLayoutEffect(() => {
    if (show) {
      openerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      return
    }

    const opener = openerRef.current
    openerRef.current = null

    if (!opener) return

    const active = document.activeElement
    const shouldRestoreFocus =
      active === null ||
      active === document.body ||
      Boolean(containerRef.current?.contains(active))

    if (shouldRestoreFocus) {
      opener.focus()
    }
  }, [show, containerRef])
}
